import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "email"]

  async authenticate() {
    this.statusTarget.textContent = "Starting authentication..."
    this.statusTarget.className = "text-sm text-slate-500 mt-2"

    try {
      const email = this.hasEmailTarget ? this.emailTarget.value : ""

      // Get auth options from server, scoped to email
      const optionsResponse = await fetch("/admin/passkey_session/auth_options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      })
      const options = await optionsResponse.json()

      // Decode challenge and credential IDs
      options.challenge = this.base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(cred => ({
          ...cred,
          id: this.base64urlToBuffer(cred.id)
        }))
      }

      this.statusTarget.textContent = "Touch your fingerprint or use Face ID..."

      // Get credential via browser API
      const credential = await navigator.credentials.get({ publicKey: options })

      // Send to server
      const authResponse = await fetch("/admin/passkey_session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: this.bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              authenticatorData: this.bufferToBase64url(credential.response.authenticatorData),
              clientDataJSON: this.bufferToBase64url(credential.response.clientDataJSON),
              signature: this.bufferToBase64url(credential.response.signature),
              userHandle: credential.response.userHandle
                ? this.bufferToBase64url(credential.response.userHandle)
                : null
            }
          }
        })
      })

      const result = await authResponse.json()

      if (result.redirect_to) {
        window.location.href = result.redirect_to
      } else if (result.error) {
        this.statusTarget.textContent = result.error
        this.statusTarget.className = "text-sm text-red-600 mt-2"
      }
    } catch (e) {
      this.statusTarget.textContent = e.name === "NotAllowedError"
        ? "Authentication cancelled."
        : `Error: ${e.message}`
      this.statusTarget.className = "text-sm text-red-600 mt-2"
    }
  }

  base64urlToBuffer(base64url) {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64 + "=".repeat((4 - base64.length % 4) % 4)
    const binary = atob(padded)
    return Uint8Array.from(binary, c => c.charCodeAt(0)).buffer
  }

  bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    bytes.forEach(b => binary += String.fromCharCode(b))
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  }
}
